/**
 * Modale de détail/édition d'une unité de connaissance.
 * Partagée entre KnowledgePage, VideoDetailPage (onglet Q&R) et AgentDraftReviewPage.
 * Extrait de KnowledgePage.js (refactor 2026-06-12) — zéro changement de logique.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Question, SpinnerGap, VideoCamera, Clock, X, ChatCircle, Trash, PencilSimple, Check, CaretLeft, CaretRight, Lightbulb, Shield } from "@phosphor-icons/react";
import { toast } from "sonner";
import { confirmDialog } from "../../lib/uiDialogs";
import { useLang } from "../../contexts/LanguageContext";
import { API_BASE as API } from "../../lib/apiBase";
import { TYPE_STYLES, formatTitleHistoryEntry } from "../../lib/knowledgeFormat";

/**
 * @param {{
 *   unit: object,
 *   videoTitle?: string,
 *   formatTimestamp?: (n: number) => string,
 *   onClose: () => void,
 *   isAdmin?: boolean,
 *   onUpdated?: (u: object) => void,
 *   onDeleted?: () => void,
 *   onNavigatePrev?: () => void,
 *   onNavigateNext?: () => void,
 *   navigationLabel?: string,
 * }} props
 *
 * Enhancement B621.b (Eric 2026-07-30) — Callbacks optionnels `onNavigatePrev`
 * et `onNavigateNext` : quand fournis, on affiche des boutons ← / → dans le
 * header du modal pour naviguer entre les items siblings (par ex. tous les
 * Q&R de la même vidéo). Permet à l'apprenant de parcourir 10-20 items en
 * session de révision sans devoir fermer et rouvrir. `navigationLabel` est
 * un texte optionnel affiché entre les 2 boutons (ex. "3 / 35").
 */
export function KnowledgeDetailModal({ unit, videoTitle, formatTimestamp, onClose, isAdmin, onUpdated, onDeleted, onNavigatePrev, onNavigateNext, navigationLabel }) {
  // 2026-05-24 hotfix — `lang` was used in saveEdit/generateAutoTitle/clearTitleHistory
  // without being defined inside this component, causing a ReferenceError that triggered
  // the CRA DevServer red error overlay ("page noire écriture rouge") when saving a Q&R.
  const { lang, tr } = useLang();
  // Eric (2026-02-10 — Vague 1 P2) — Support édition Scripts de vente via
  // cette même modale. Les scripts vivent dans `sales_scripts` (collection
  // séparée), donc les endpoints diffèrent. On centralise ici les URLs pour
  // brancher `saveEdit / saveTurns / approve|reject / regen title / history`
  // sur le bon service selon `unit.__isScriptDoc`.
  const isScript = Boolean(unit.__isScriptDoc);
  const ep = {
    patch: isScript ? `${API}/api/scripts/${unit.id}` : `${API}/api/agent/knowledge-units/${unit.id}`,
    history: isScript ? `${API}/api/scripts/${unit.id}/history` : `${API}/api/agent/knowledge-units/${unit.id}/history`,
    generateTitle: isScript ? `${API}/api/scripts/${unit.id}/regenerate-title` : `${API}/api/agent/knowledge-units/${unit.id}/generate-title`,
  };
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(true);
  const [posting, setPosting] = useState(false);
  // Vincent #27 (Eric 2026-06-04) — Édition key_points (liste d'items) pour Concept
  const [editKeyPoints, setEditKeyPoints] = useState(unit.content?.key_points || []);
  // Vincent XLS iter103 (item 19) — Édition des tours v2/v3 des scripts de vente.
  // `editingTurnsVersion` ∈ {null, "v1", "v2", "v3"} indique quel accordéon est en mode édition.
  // Vincent B814b (Eric 2026-02-13) — V1 ajouté pour restaurer l'édition complète (B814).
  // `draftTurns` est la copie de travail (array de {role, text, ...}) sauvegardée via PATCH.
  const [editingTurnsVersion, setEditingTurnsVersion] = useState(null);
  const [draftTurns, setDraftTurns] = useState([]);
  const [savingTurns, setSavingTurns] = useState(false);
  // Vincent B814b (Eric 2026-02-13) — Édition du tableau de validation
  // (perdue lors de refacto B814). Colonnes : time_code, citation_originale,
  // phrase_reformule, justification, objection_evitee.
  const [editingValidation, setEditingValidation] = useState(false);
  const [validationDraft, setValidationDraft] = useState([]);
  const [savingValidation, setSavingValidation] = useState(false);

  const openTurnsEditor = (version) => {
    // Vincent B814b (Eric 2026-02-13) — Support V1 en plus de V2/V3 pour
    // restaurer l'édition complète du script (perdue lors de refacto B814).
    const fieldByVersion = {
      v1: "v1_original",
      v2: "v2_reformule",
      v3: "v3_justifie",
    };
    const field = fieldByVersion[version] || "v3_justifie";
    const src = (displayUnit.content?.[field]) || (unit.content?.[field]) || [];
    // Préserve time_code / justification / objection_evitee (V3) et start_seconds
    // pour ne pas les perdre lors d'une simple édition de texte.
    setDraftTurns(src.map((t) => ({
      role: t.role || "",
      speaker: t.speaker || "",
      time_code: t.time_code || "",
      start_seconds: t.start_seconds,
      text: t.text || t.content || "",
      justification: t.justification || "",
      objection_evitee: t.objection_evitee || "",
    })));
    setEditingTurnsVersion(version);
  };

  const updateDraftTurn = (idx, patch) => {
    setDraftTurns((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };
  const addDraftTurn = () => setDraftTurns((p) => [...p, { role: "", text: "" }]);
  const removeDraftTurn = (idx) => setDraftTurns((p) => p.filter((_, i) => i !== idx));
  const moveDraftTurn = (idx, dir) => {
    setDraftTurns((p) => {
      const next = [...p];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return p;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const saveTurns = async () => {
    if (!editingTurnsVersion) return;
    setSavingTurns(true);
    try {
      // Vincent B814b (Eric 2026-02-13) — Préserve les métadonnées (time_code,
      // justification, objection_evitee, speaker, start_seconds) et supporte V1.
      const isV3 = editingTurnsVersion === "v3";
      const cleaned = (draftTurns || [])
        .map((t) => {
          const base = {
            role: (t.role || "").trim(),
            text: (t.text || "").trim(),
          };
          if (t.speaker) base.speaker = String(t.speaker).trim();
          if (t.time_code) base.time_code = String(t.time_code).trim();
          if (typeof t.start_seconds === "number") base.start_seconds = t.start_seconds;
          if (isV3) {
            base.justification = (t.justification || "").trim();
            base.objection_evitee = (t.objection_evitee || "").trim();
          }
          return base;
        })
        .filter((t) => t.text);
      const fieldByVersion = {
        v1: "v1_original",
        v2: "v2_reformule",
        v3: "v3_justifie",
      };
      const field = fieldByVersion[editingTurnsVersion] || "v3_justifie";
      const payload = { [field]: cleaned };
      await axios.patch(ep.patch, payload, { withCredentials: true });
      toast.success(lang === "fr"
        ? `Version ${editingTurnsVersion.toUpperCase()} mise à jour (${cleaned.length} tour${cleaned.length > 1 ? "s" : ""})`
        : `Version ${editingTurnsVersion.toUpperCase()} updated (${cleaned.length} turn${cleaned.length > 1 ? "s" : ""})`);
      const newContent = { ...(unit.content || {}), [field]: cleaned };
      const updated = { ...unit, content: newContent, edited_post_approval: true };
      onUpdated?.(updated);
      setEditingTurnsVersion(null);
      setDraftTurns([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "fr" ? "Échec de l'enregistrement" : "Save failed"));
    } finally {
      setSavingTurns(false);
    }
  };

  // Vincent B814b (Eric 2026-02-13) — Handlers pour l'édition du
  // `validation_table` d'un script (5 colonnes : time_code, citation_originale,
  // phrase_reformule, justification, objection_evitee). Le PATCH backend
  // accepte le champ `validation_table`.
  const openValidationEditor = () => {
    const src = (displayUnit.content?.validation_table) || (unit.content?.validation_table) || [];
    setValidationDraft(src.map((r) => ({
      time_code: r.time_code || "",
      citation_originale: r.citation_originale || "",
      phrase_reformule: r.phrase_reformule || "",
      justification: r.justification || "",
      objection_evitee: r.objection_evitee || "",
    })));
    setEditingValidation(true);
  };
  const updateValidationRow = (idx, patch) => {
    setValidationDraft((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const addValidationRow = () => setValidationDraft((p) => [...p, {
    time_code: "", citation_originale: "", phrase_reformule: "", justification: "", objection_evitee: "",
  }]);
  const removeValidationRow = (idx) => setValidationDraft((p) => p.filter((_, i) => i !== idx));
  const saveValidation = async () => {
    setSavingValidation(true);
    try {
      const cleaned = (validationDraft || [])
        .map((r) => ({
          time_code: (r.time_code || "").trim(),
          citation_originale: (r.citation_originale || "").trim(),
          phrase_reformule: (r.phrase_reformule || "").trim(),
          justification: (r.justification || "").trim(),
          objection_evitee: (r.objection_evitee || "").trim(),
        }))
        .filter((r) => r.phrase_reformule || r.citation_originale || r.justification);
      await axios.patch(ep.patch, { validation_table: cleaned }, { withCredentials: true });
      toast.success(lang === "fr"
        ? `Tableau de validation mis à jour (${cleaned.length} ligne${cleaned.length > 1 ? "s" : ""})`
        : `Validation table updated (${cleaned.length} row${cleaned.length > 1 ? "s" : ""})`);
      const newContent = { ...(unit.content || {}), validation_table: cleaned };
      const updated = { ...unit, content: newContent, edited_post_approval: true };
      setDisplayUnit(updated);
      onUpdated?.(updated);
      setEditingValidation(false);
      setValidationDraft([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "fr" ? "Échec de l'enregistrement" : "Save failed"));
    } finally {
      setSavingValidation(false);
    }
  };
  // Post-approval edit mode for qa (Vincent 2026-05-02 feedback: allow
  // corrections directly from the knowledge base, not only during validation).
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(unit.title || "");
  const [editQ, setEditQ] = useState(unit.content?.question || "");
  const [editA, setEditA] = useState(unit.content?.answer || "");
  // Vincent B726 (Eric 2026-02-12) — État d'édition pour les synthèses,
  // restaure la capacité d'éditer titre + body markdown + points clés
  // depuis la Base de connaissances et l'Explorateur (perdue récemment).
  const [editBody, setEditBody] = useState(unit.content?.body || "");
  const [editTakeaways, setEditTakeaways] = useState(
    Array.isArray(unit.content?.key_takeaways) ? [...unit.content.key_takeaways] : []
  );
  // Vincent XLSX 2026-07-07 22h Row #12 (Eric 2026-07-08 SPEC STRICK) —
  // Bug affichage : après "Enregistrer" une Q&R éditée, l'UI affichait
  // TEMPORAIREMENT l'ancienne version (le temps que le parent re-render
  // avec le nouveau `unit` prop). On maintient un état local `displayUnit`
  // mis à jour INSTANTANÉMENT côté client à la sauvegarde, indépendamment
  // du round-trip parent.
  const [displayUnit, setDisplayUnit] = useState(unit);
  // Vincent XLSX 2026-07-09 #1+#2 (Eric 2026-07-09 SPEC STRICK) — Optimistic
  // status pour boutons Accepter/Refuser (bascule INSTANT + retire couleur
  // du bouton opposé au clic). Reset quand un nouvel `unit.id` est passé.
  const [pendingStatus, setPendingStatus] = useState(null);
  // Vincent #3 (Eric 2026-02-06) — Case à cocher "tous quiz" individuelle, remplace
  // le bouton bulk générique. Si non cochée : Q&R réservée aux quiz portant
  // uniquement sur cette vidéo. Cochée : Q&R éligible aux quiz tous-thèmes.
  const [editQuizEligible, setEditQuizEligible] = useState(Boolean(unit.quiz_eligible));
  // Vincent #172 (Eric 2026-05-26 round 4) — Resync local edit state when the
  // parent passes a NEW `unit` reference (e.g. after save → parent re-renders
  // with the updated KU). Without this, the modal reopens with the OLD title
  // even though the backend has the new one ("le titre est toujours le même
  // qu'au départ" — Vincent screenshot).
  useEffect(() => {
    setEditTitle(unit.title || "");
    setEditQ(unit.content?.question || "");
    setEditA(unit.content?.answer || "");
    setEditKeyPoints(unit.content?.key_points || []);
    // Vincent B726 (Eric 2026-02-12) — Resync body + takeaways pour synthèses
    setEditBody(unit.content?.body || "");
    setEditTakeaways(Array.isArray(unit.content?.key_takeaways) ? [...unit.content.key_takeaways] : []);
    setEditQuizEligible(Boolean(unit.quiz_eligible));
    setDisplayUnit(unit);
    setPendingStatus(null);
    setTitleManuallyEdited(false);
  }, [unit.id, unit.title, unit.content?.question, unit.content?.answer, unit.content?.key_points, unit.quiz_eligible, unit]);
  // Tracks whether the user has manually edited the title — once they have, we
  // stop auto-deriving it from the question (Vincent feedback: "le titre est
  // une version tronquée de la Q et n'est pas modifiable").
  const [titleManuallyEdited, setTitleManuallyEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingTitle, setGeneratingTitle] = useState(false);
  // Vincent #110 (2026-05-08) — propositions de titre + historique cliquable.
  const [titleProposals, setTitleProposals] = useState([]);
  const [titleHistory, setTitleHistory] = useState([]);
  // 2026-05-24 hotfix — Modal de confirmation propre pour "Tout effacer" (remplace confirm() natif)
  const [showClearHistoryConfirm, setShowClearHistoryConfirm] = useState(false);
  // Edit-history viewer (Loi 25 audit trail)
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const loadHistory = async () => {
    if (history !== null) { setShowHistory(true); return; }
    setLoadingHistory(true);
    try {
      const { data } = await axios.get(
        ep.history,
        { withCredentials: true }
      );
      setHistory(data.edit_history || []);
      setShowHistory(true);
    } catch (e) {
      // Open the panel with an empty state instead of silently failing.
      setHistory([]);
      setShowHistory(true);
      if (e.response?.status !== 404) {
        toast.error(tr("Échec du chargement de l'historique", "Could not load the history"));
      }
    } finally {
      setLoadingHistory(false);
    }
  };

  const saveEdit = async () => {
    const t = editTitle.trim();
    const q = editQ.trim();
    const a = editA.trim();
    const utype = (unit.type || "qa").toLowerCase();

    // Vincent (Eric 2026-05-16) — Polymorphic save: send the right fields per type.
    // editQ is reused as a "secondary" input (question for qa, summary for procedure),
    // editA as the main body (answer / text / definition / response).
    // Eric (2026-02-10 — Vague 1 P2) — Scripts (`__isScriptDoc`) : le backend
    // `/api/scripts/{id}` n'accepte QUE title / v2 / v3 / validation / status /
    // notes. On restreint donc le payload au titre ici (l'édition v2/v3 se
    // fait via TurnsInlineEditor + `saveTurns`).
    const payload = {};
    if (isScript) {
      // Vincent B814b (Eric 2026-02-13) — Support édition scenario en plus
      // du titre. Le PATCH backend accepte désormais `scenario`.
      // Les turns V1/V2/V3 + validation_table passent par saveTurns/saveValidation.
      if (typeof editQ === "string") {
        const s = editQ.trim();
        if (s !== (unit.content?.scenario || "").trim()) {
          payload.scenario = s;
        }
      }
    } else if (utype === "qa") {
      if (!q || !a) { toast.error(tr("Question et réponse sont requises", "Question and answer are both required")); return; }
      payload.question = q; payload.answer = a;
      // Vincent #3 — Inclure quiz_eligible dans le PATCH
      payload.quiz_eligible = editQuizEligible;
    } else if (utype === "procedure" || utype === "process") {
      if (!a && !q) { toast.error(tr("Résumé ou étapes requis", "Summary or steps required")); return; }
      if (q) payload.summary = q;
      if (a) {
        // Steps: split on newlines or "- " bullets
        const steps = a.split(/\r?\n+/).map(s => s.replace(/^[-•\d.)\s]+/, "").trim()).filter(Boolean);
        if (steps.length) payload.steps = steps;
      }
    } else if (utype === "script") {
      if (!a) { toast.error(tr("Texte du script requis", "Script text required")); return; }
      if (q) payload.scenario = q;
      payload.text = a;
    } else if (utype === "objection") {
      if (!q && !a) { toast.error(tr("Objection et/ou réponse requise", "Objection and/or response required")); return; }
      if (q) payload.question = q;   // reuse `question` field as "objection text" placeholder
      if (a) payload.response = a;
    } else if (utype === "concept") {
      if (!a) { toast.error(tr("Définition requise", "Definition required")); return; }
      payload.definition = a;
      // Vincent #27 — inclure key_points modifiés
      payload.key_points = (editKeyPoints || []).filter(s => s && s.trim());
    } else if (utype === "synthesis") {
      // Vincent B726 (Eric 2026-02-12) — Édition d'une synthèse : body
      // markdown obligatoire, points clés optionnels (filtre les entrées
      // vides pour éviter les artefacts).
      const b = editBody.trim();
      if (!b) { toast.error(tr("Corps de la synthèse requis", "Synthesis body required")); return; }
      payload.body = b;
      payload.key_takeaways = (editTakeaways || [])
        .map(s => (typeof s === "string" ? s.trim() : ""))
        .filter(s => s.length > 0);
    }

    const finalTitle = t || (q.slice(0, 100) || unit.title || "Sans titre");
    payload.title = finalTitle;

    setSaving(true);
    try {
      const { data } = await axios.patch(
        ep.patch,
        payload,
        { withCredentials: true }
      );
      toast.success(lang === "fr" ? "Connaissance mise à jour" : "Knowledge updated");
      const newContent = { ...(unit.content || {}) };
      if (payload.question) newContent.question = payload.question;
      if (payload.answer) newContent.answer = payload.answer;
      if (payload.summary) newContent.summary = payload.summary;
      if (payload.steps) newContent.steps = payload.steps;
      if (payload.text) { newContent.text = payload.text; newContent.content = payload.text; }
      if (payload.scenario !== undefined) newContent.scenario = payload.scenario;
      if (payload.response) { newContent.response = payload.response; newContent.phrase_evitement = payload.response; }
      if (payload.definition) newContent.definition = payload.definition;
      if (payload.key_points) newContent.key_points = payload.key_points;
      const updated = { ...unit, title: finalTitle, content: newContent, edited_post_approval: true };
      // Vincent XLSX 2026-07-07 22h Row #12 (Eric 2026-07-08) — Mise à jour
      // LOCALE instantanée de displayUnit AVANT le round-trip parent → plus
      // d'affichage de l'ancienne version en READ mode.
      setDisplayUnit(updated);
      onUpdated?.(updated);
      await refreshTitleHistory();
      // Vincent #172 (Eric 2026-05-26 round 4) — Refresh full edit history
      // so the panel reflects what we just saved (was "5 historiques mais
      // je ne suis pas capable de les voir").
      try {
        const { data: histData } = await axios.get(
          ep.history,
          { withCredentials: true }
        );
        setHistory(histData.edit_history || []);
      } catch { /* non-fatal */ }
      setEditing(false);
      setTitleManuallyEdited(false);
      void data;
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "fr" ? "Échec de la mise à jour" : "Update failed"));
    } finally {
      setSaving(false);
    }
  };

  // Vincent 2026-05-04: bouton "Générer un titre auto" — synthèse 12-15 mots
  // au lieu de la question tronquée. Appelle GPT-5.2 côté backend, pré-remplit
  // l'input, l'admin peut éditer puis sauver.
  const generateAutoTitle = async () => {
    if (generatingTitle) return;
    setGeneratingTitle(true);
    try {
      const { data } = await axios.post(
        ep.generateTitle,
        {},
        { withCredentials: true, timeout: 30000 }
      );
      // Vincent #110 — exposer les 3 propositions + historique
      setTitleProposals(Array.isArray(data?.proposals) ? data.proposals : (data?.title ? [data.title] : []));
      setTitleHistory(Array.isArray(data?.history) ? data.history : []);
      // Vincent #213 (Eric 2026-06-04) — Afficher erreur explicite si IA a échoué
      // (avant : silence quand proposals vide → utilisateur ne sait pas pourquoi).
      if ((!data?.proposals || data.proposals.length === 0) && data?.error) {
        toast.error(`Échec génération IA : ${data.error}`);
        return;
      }
      // Backward-compat : pré-remplir l'input avec la 1ère proposition
      const firstTitle = (data?.proposals && data.proposals[0]) || data?.title;
      if (firstTitle) {
        setEditTitle(firstTitle);
        setTitleManuallyEdited(true);
        toast.success(data?.proposals?.length > 1
          ? `${data.proposals.length} propositions générées — choisissez ou éditez ci-dessous`
          : "Titre généré — éditez si nécessaire puis sauvegardez");
      } else {
        toast.error(tr(
          "Aucune proposition générée par l'IA — réessayez ou vérifiez votre Forfait AI",
          "The AI returned no suggestion — try again or check your AI Package",
        ));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Échec de la génération de titre", "Title generation failed"));
    } finally {
      setGeneratingTitle(false);
    }
  };

  // Vincent #110 — Restaurer un titre antérieur depuis l'historique
  const restoreTitleFromHistory = (historicalTitle) => {
    if (!historicalTitle) return;
    setEditTitle(historicalTitle);
    setTitleManuallyEdited(true);
    toast.info(tr(
      "Titre restauré depuis l'historique — sauvegardez pour confirmer",
      "Title restored from the history — save to confirm",
    ));
  };

  // Vincent #110 — Effacer toute la pile d'historique des titres
  // 2026-05-24 hotfix : remplace `window.confirm()` (bloqué dans l'iframe Preview Emergent
  // — "rien ne se passe quand je clique") par un modal contrôlé via state.
  const clearTitleHistory = async () => {
    setShowClearHistoryConfirm(true);
  };
  const handleClearHistoryConfirmed = async () => {
    setShowClearHistoryConfirm(false);
    try {
      await axios.delete(`${API}/api/agent/knowledge-units/${unit.id}/title-history`, { withCredentials: true });
      setTitleHistory([]);
      toast.success(lang === "fr" ? "Historique des titres effacé" : "Title history cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "fr" ? "Échec de l'effacement" : "Clear failed"));
    }
  };

  // Vincent #110 (2026-05-12 fix) — Helper réutilisable : recharge l'historique
  // des titres depuis le serveur. Appelé à l'ouverture du modal ET après chaque
  // sauvegarde de titre pour que la liste reflète immédiatement la nouvelle
  // entrée. Auparavant `useEffect([unit.id])` ne se redéclenchait pas car
  // l'id de l'unit ne change pas pendant l'édition.
  const refreshTitleHistory = async () => {
    try {
      const { data } = await axios.get(ep.history, { withCredentials: true });
      const editHistory = data?.edit_history || [];
      const titleEntries = editHistory
        .filter(e => (e.fields_changed || []).includes("title") && (e.previous_title || "").trim())
        .map(e => ({ title: e.previous_title, replaced_at: e.edited_at, replaced_by_email: e.edited_by_email }));
      setTitleHistory(titleEntries.slice(-20));
    } catch { /* silent — history is optional */ }
  };

  useEffect(() => {
    loadComments();
    // Vincent #110 — Pré-charger l'historique des titres dès l'ouverture du modal
    // pour que le panneau "Historique (N)" soit visible sans avoir à cliquer "Générer".
    refreshTitleHistory();
  }, [unit.id]);

  const loadComments = async () => {
    // Eric (2026-02-10 — Vague 1 P2) — Scripts n'ont pas d'endpoint
    // commentaires (voir section masquée dans le JSX). On saute proprement.
    if (isScript) { setLoadingComments(false); return; }
    try {
      const { data } = await axios.get(`${API}/api/knowledge/${unit.id}/comments`, { withCredentials: true });
      setComments(data);
    } catch { /* non-fatal — laisse la liste vide */ } finally { setLoadingComments(false); }
  };

  const postComment = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      const { data } = await axios.post(`${API}/api/knowledge/${unit.id}/comments`, { text: newComment }, { withCredentials: true });
      setComments([data, ...comments]);
      setNewComment("");
    } catch { /* non-fatal — l'utilisateur peut réessayer */ } finally { setPosting(false); }
  };

  const deleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/api/knowledge/comments/${commentId}`, { withCredentials: true });
      setComments(comments.filter(c => c.id !== commentId));
    } catch { /* non-fatal — la liste sera resync au prochain fetch */ }
  };

  // Vincent (Eric 2026-05-16, round 2) — Cancel reset selon le type
  // (réinitialise editQ / editA aux valeurs originales du content).
  const resetEditFieldsToOriginal = () => {
    const t = (unit.type || "").toLowerCase();
    setEditTitle(unit.title || "");
    if (t === "qa") {
      setEditQ(unit.content?.question || "");
      setEditA(unit.content?.answer || "");
    } else if (t === "procedure" || t === "process") {
      setEditQ(unit.content?.summary || "");
      const steps = unit.content?.steps || [];
      setEditA(steps.map((s, i) => `${i + 1}. ${s}`).join("\n"));
    } else if (t === "script") {
      setEditQ(unit.content?.scenario || "");
      setEditA(unit.content?.text || unit.content?.content || "");
    } else if (t === "objection") {
      setEditQ(unit.content?.objection || unit.content?.question || "");
      setEditA(unit.content?.response || unit.content?.phrase_evitement || "");
    } else if (t === "concept") {
      setEditQ("");
      setEditA(unit.content?.definition || "");
    }
    setTitleManuallyEdited(false);
  };

  // ===== Reusable helpers (Vincent 2026-05-16) =====
  // Bloc d'édition du titre + IA + propositions + historique — partagé entre
  // tous les types (qa, procedure, script, objection, concept).
  const renderTitleEditBlock = () => {
    if (!editing) return null;
    return (
      <div>
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-sm font-medium text-muted-foreground">Titre</h4>
          <button
            type="button"
            onClick={generateAutoTitle}
            disabled={generatingTitle}
            className="text-xs px-2 py-1 rounded-md bg-[#002FA7]/10 text-[#002FA7] hover:bg-[#002FA7]/20 disabled:opacity-50 transition-colors flex items-center gap-1"
            data-testid="generate-auto-title-btn"
            title={tr("Générer un titre synthétique de 12-15 mots via IA (GPT-5.2)", "Generate a 12-15 word summary title with AI (GPT-5.2)")}
          >
            {generatingTitle ? "⏳ Génération..." : "✨ Générer titre auto"}
          </button>
        </div>
        <input
          type="text"
          value={editTitle}
          onChange={(e) => { setEditTitle(e.target.value); setTitleManuallyEdited(true); }}
          maxLength={200}
          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm font-semibold focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
          data-testid="edit-ku-title"
          placeholder={tr("Titre synthétique (8-14 mots)", "Summary title (8-14 words)")}
        />
        {titleProposals.length > 1 && (
          <div className="mt-2 space-y-1" data-testid="ku-title-proposals">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">{tr("Propositions IA — cliquez pour utiliser", "AI suggestions — click to use")}</p>
            {titleProposals.map((p, i) => (
              <button
                key={i}
                type="button"
                onClick={() => { setEditTitle(p); setTitleManuallyEdited(true); }}
                className="w-full text-left px-2 py-1.5 text-xs rounded border border-zinc-200 bg-white hover:bg-blue-50 hover:border-blue-300 transition-colors"
                data-testid={`ku-title-proposal-${i}`}
              >
                <span className="text-blue-600 mr-1">→</span>{p}
              </button>
            ))}
          </div>
        )}
        <div className="mt-2 space-y-1" data-testid="ku-title-history">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
              🕐 Historique ({titleHistory.length})
            </p>
            <button
              type="button"
              onClick={clearTitleHistory}
              disabled={titleHistory.length === 0}
              className="text-[10px] text-rose-600 hover:underline disabled:text-zinc-300 disabled:cursor-not-allowed disabled:no-underline"
              data-testid="ku-title-history-clear"
              title={titleHistory.length === 0
                ? tr("Aucun historique à effacer", "No history to clear")
                : tr("Effacer définitivement tout l'historique", "Permanently clear the whole history")}
            >
              🗑 Tout effacer
            </button>
          </div>
          {titleHistory.length === 0 ? (
            <p className="text-[10px] text-zinc-400 italic px-2 py-1.5">
              Aucun titre précédent enregistré. L&apos;historique se peuplera automatiquement à chaque sauvegarde de nouveau titre.
            </p>
          ) : (
            titleHistory.slice().reverse().map((h, i) => (
              <button
                key={i}
                type="button"
                onClick={() => restoreTitleFromHistory(h.title)}
                className="w-full text-left px-2 py-1.5 text-xs rounded border border-zinc-100 bg-zinc-50 hover:bg-amber-50 hover:border-amber-300 transition-colors"
                data-testid={`ku-title-history-${i}`}
              >
                <span className="text-amber-600 mr-1">⟲</span>
                {/* Vincent 20/05/2026 — Format imposé : "AAAA-MM-JJ HH:MM : titre" */}
                {h.replaced_at && (
                  <span className="text-zinc-500 font-mono text-[10.5px] mr-1">
                    {formatTitleHistoryEntry(h.replaced_at)} :
                  </span>
                )}
                <span>{h.title}</span>
              </button>
            ))
          )}
        </div>
        <p className="text-[10px] text-muted-foreground mt-0.5">
          {titleManuallyEdited
            ? tr("Vous avez modifié manuellement le titre — il restera tel quel.", "You edited the title by hand — it stays as is.")
            : tr("Astuce : utilisez « ✨ Générer titre auto » pour produire une synthèse IA, ou modifiez ici.", "Tip: use “✨ Generate title” for an AI summary, or edit it here.")}
          {" "}({editTitle.length}/200)
        </p>
      </div>
    );
  };

  // Boutons Annuler / Enregistrer — partagés.
  const renderEditActions = () => {
    if (!editing) return null;
    return (
      <div className="flex gap-2 justify-end pt-2 border-t border-border">
        <Button
          variant="outline"
          size="sm"
          onClick={() => { setEditing(false); resetEditFieldsToOriginal(); }}
          disabled={saving}
          data-testid="cancel-edit-ku"
        >
          <X size={14} className="mr-1" /> Annuler
        </Button>
        <Button
          size="sm"
          onClick={saveEdit}
          disabled={saving}
          className="bg-teal-600 hover:bg-teal-700 text-white"
          data-testid="save-edit-ku"
        >
          {saving ? <SpinnerGap size={14} className="mr-1 animate-spin" /> : <Check size={14} className="mr-1" />}
          {tr("Enregistrer", "Save")}
        </Button>
      </div>
    );
  };

  const renderContent = () => {
    switch (unit.type) {
      case "qa":
        return (
          <div className="space-y-4">
            {renderTitleEditBlock()}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-1">Question</h4>
              {editing ? (
                <textarea
                  value={editQ}
                  onChange={(e) => setEditQ(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-base focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                  data-testid="edit-ku-question"
                />
              ) : (
                <p className="text-base">{displayUnit.content?.question}</p>
              )}
            </div>
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-1">{tr("Réponse", "Answer")}</h4>
              {editing ? (
                <textarea
                  value={editA}
                  onChange={(e) => setEditA(e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-base focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                  data-testid="edit-ku-answer"
                />
              ) : (
                <p className="text-base">{displayUnit.content?.answer}</p>
              )}
            </div>
            {/* Vincent #3 (Eric 2026-02-06) — Case à cocher "tous quiz" PAR
                question, remplaçant le bouton bulk générique. */}
            {editing && unit.type === "qa" && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <input
                  type="checkbox"
                  id={`quiz-eligible-${unit.id}`}
                  checked={editQuizEligible}
                  onChange={(e) => setEditQuizEligible(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                  data-testid="edit-ku-quiz-eligible"
                />
                <label htmlFor={`quiz-eligible-${unit.id}`} className="text-sm cursor-pointer flex-1">
                  <span className="font-semibold text-amber-900">🎯 {tr("Tous quiz", "All quizzes")}</span>
                  <span className="block text-xs text-amber-700 mt-0.5">
                    Cochée : la question est généraliste et peut servir dans tous les quiz, même incluant d&apos;autres vidéos.
                    Décochée : la question reste propre à cette vidéo (utilisée uniquement dans les quiz portant sur cette vidéo).
                  </span>
                </label>
              </div>
            )}
            {renderEditActions()}
            {showHistory && (
              <div className="mt-4 pt-4 border-t border-border" data-testid="ku-history-panel">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-bold text-amber-700">Historique des éditions ({history?.length || 0})</h4>
                  <div className="flex items-center gap-2">
                    {/* Suggestion Eric (2026-05-08) — Bouton pour effacer l'historique des réponses (question/answer)
                        sans toucher à l'historique des titres. Idéal pour nettoyer une Q&R retravaillée 10 fois. */}
                    {(history || []).some(h => (h.fields_changed || []).some(f => f === "question" || f === "answer")) && (
                      <button
                        onClick={async () => {
                          const ok = await confirmDialog(
                            tr(
                              "Effacer définitivement l'historique des anciennes versions de question/réponse ? L'historique des titres et la version actuelle seront conservés.",
                              "Permanently clear the history of previous question/answer versions? The title history and the current version are kept.",
                            ),
                            { danger: true, confirmLabel: "Effacer" },
                          );
                          if (!ok) return;
                          try {
                            const { data } = await axios.delete(`${API}/api/agent/knowledge-units/${unit.id}/answer-history`, { withCredentials: true });
                            setHistory((prev) => (prev || []).filter(h => !((h.fields_changed || []).some(f => f === "question" || f === "answer") && !(h.fields_changed || []).includes("title"))));
                            toast.success(`Historique des réponses effacé (${data.remaining_history_entries} entrée(s) restante(s))`);
                          } catch (e) {
                            toast.error(e.response?.data?.detail || tr("Échec de l'effacement", "Deletion failed"));
                          }
                        }}
                        className="text-[10px] text-rose-600 hover:underline flex items-center gap-1"
                        data-testid="ku-answer-history-clear"
                        title={tr("Efface uniquement les snapshots question/réponse — conserve l'historique des titres", "Clears only the question/answer snapshots — keeps the title history")}
                      >
                        🗑 {tr("Effacer historique des réponses", "Clear answer history")}
                      </button>
                    )}
                    <button onClick={() => setShowHistory(false)} className="text-[10px] text-muted-foreground hover:underline">
                      {tr("Fermer", "Close")}
                    </button>
                  </div>
                </div>
                {(!history || history.length === 0) ? (
                  <p className="text-xs text-muted-foreground italic">{tr("Aucune édition enregistrée.", "No edit recorded.")}</p>
                ) : (
                  <ul className="space-y-3 max-h-72 overflow-y-auto pr-2">
                    {/* Vincent #172 (Eric 2026-05-28 round 5) — Vincent veut voir
                        UNIQUEMENT les renommages utiles (un vrai ancien titre).
                        On filtre donc les entries où previous_content.title ET
                        new_content.title sont vides — ces snapshots n'apportent
                        AUCUNE info au concepteur (data résiduelle de schémas
                        antérieurs).
                        Pour les renommages où SEUL le previous est vide (= 1er
                        titre attribué), on affiche un libellé contextuel
                        "Premier titre attribué" plutôt que "(titre vide)". */}
                    {[...history].reverse().filter((h) => {
                      const fields = (h.fields_changed || []).filter(f => f);
                      const isTitleOnly = fields.length === 1 && fields[0] === "title";
                      if (!isTitleOnly) return true;
                      const prev = (h.previous_content?.title || "").trim();
                      const next = (h.new_content?.title || "").trim();
                      // Skip si AUCUN des deux n'apporte d'info
                      return !!(prev || next);
                    }).map((h, i) => {
                      // Vincent B870 (Eric 2026-02-14 SPEC STRICK PROD) —
                      // Fallback : quand `fields_changed` est vide (entry
                      // historique corrompue / provenant d'un schéma
                      // antérieur), on recalcule les diffs en comparant
                      // les clés de previous_content et new_content pour
                      // que l'utilisateur voit AU MOINS quels champs ont
                      // bougé au lieu d'un « Champs modifiés : » vide.
                      let fields = (h.fields_changed || []).filter(f => f);
                      if (fields.length === 0) {
                        const prev = h.previous_content || {};
                        const next = h.new_content || {};
                        const allKeys = new Set([...Object.keys(prev), ...Object.keys(next)]);
                        fields = Array.from(allKeys).filter(k => {
                          const p = prev[k];
                          const n = next[k];
                          // Comparaison JSON-safe pour arrays/objets (steps, etc.)
                          return JSON.stringify(p) !== JSON.stringify(n);
                        });
                      }
                      const isTitleOnly = fields.length === 1 && fields[0] === "title";
                      const otherFields = fields.filter(f => f !== "title");
                      const prevTitle = (h.previous_content?.title || "").trim();
                      const nextTitle = (h.new_content?.title || "").trim();
                      return (
                      <li key={i} className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs" data-testid={`edit-history-item-${i}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="font-mono text-[10px] text-amber-900">
                            {/* Vincent B870 v2 (Eric 2026-02-14 SPEC STRICK PROD) —
                                Guard `Invalid Date` : anciennes entrées d'historique
                                peuvent avoir `edited_at` manquant ou non-parsable. */}
                            {(() => {
                              const ts = h.edited_at || h.created_at;
                              const d = ts ? new Date(ts) : null;
                              return d && !isNaN(d.getTime())
                                ? d.toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" })
                                : "— date inconnue";
                            })()}
                          </span>
                          <span className="text-[10px] text-amber-700">par {h.edited_by_email || h.edited_by}</span>
                        </div>
                        {isTitleOnly ? (
                          <>
                            <div className="text-[10px] uppercase tracking-wide font-bold text-zinc-600 mb-1">
                              {prevTitle ? "Renommage du titre" : "Premier titre attribué"}
                            </div>
                            {prevTitle && (
                              <div className="bg-red-50 border-l-2 border-red-300 pl-2 py-1 text-[11px] line-through text-red-900 mb-1">
                                {prevTitle}
                              </div>
                            )}
                            <div className="bg-teal-50 border-l-2 border-teal-400 pl-2 py-1 text-[11px] text-teal-900 font-medium">
                              {nextTitle || <em className="text-muted-foreground">{tr("— titre effacé —", "— title cleared —")}</em>}
                            </div>
                          </>
                        ) : fields.length === 0 ? (
                          <div className="text-[11px] italic text-muted-foreground" data-testid={`edit-history-empty-${i}`}>
                            Édition enregistrée sans détail de champ (probablement un ré-enregistrement sans modification réelle).
                          </div>
                        ) : (
                          <>
                            <div className="text-[10px] text-muted-foreground mb-1.5">
                              Champs modifiés : <span className="font-semibold">{fields.join(", ")}</span>
                            </div>
                            {otherFields.map((field) => (
                              <div key={field} className="mt-1.5">
                                <div className="text-[9px] uppercase tracking-wide font-bold text-zinc-600 mb-0.5">{field}</div>
                                <div className="bg-red-50 border-l-2 border-red-300 pl-2 py-1 text-[11px] line-through text-red-900 mb-1">
                                  {h.previous_content?.[field] || <em className="text-muted-foreground">vide</em>}
                                </div>
                                <div className="bg-teal-50 border-l-2 border-teal-400 pl-2 py-1 text-[11px] text-teal-900">
                                  {h.new_content?.[field] || <em className="text-muted-foreground">vide</em>}
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        );
      case "procedure":
      case "process":
        return (
          <div className="space-y-4">
            {renderTitleEditBlock()}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-1">{tr("Résumé / Contexte", "Summary / Context")}</h4>
              {editing ? (
                <textarea
                  value={editQ}
                  onChange={(e) => setEditQ(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-base focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                  data-testid="edit-ku-procedure-summary"
                  placeholder={tr("Résumé court de la procédure (optionnel)", "Short summary of the procedure (optional)")}
                />
              ) : (
                displayUnit.content?.summary && <p className="text-base">{displayUnit.content.summary}</p>
              )}
            </div>
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-2">{tr("Étapes", "Steps")}</h4>
              {editing ? (
                <>
                  <textarea
                    value={editA}
                    onChange={(e) => setEditA(e.target.value)}
                    rows={8}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm font-mono focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                    data-testid="edit-ku-procedure-steps"
                    placeholder={tr("1. Première étape\n2. Deuxième étape\n3. ...", "1. First step\n2. Second step\n3. ...")}
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">{tr("Une étape par ligne. Les puces et numéros sont retirés automatiquement à la sauvegarde.", "One step per line. Bullets and numbers are stripped automatically on save.")}</p>
                </>
              ) : (
                <ol className="space-y-2">
                  {displayUnit.content?.steps?.map((step, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center flex-shrink-0">
                        {i + 1}
                      </span>
                      <span className="text-sm pt-0.5">{step}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
            {renderEditActions()}
          </div>
        );
      case "script":
        // Vincent #249 (2026-02-06) — Si le script de vente a un pipeline
        // v1/v2/v3/validation (généré par le LLM), on affiche les 4 accordéons
        // comme dans /dashboard/scripts pour cohérence. Sinon (script importé
        // .docx ou legacy), on garde le rendu single-text.
        //
        // Vincent B814b (Eric 2026-02-13) — Bug 814 : quand on clique
        // « ✏️ Éditer » dans la barre du modal, la vue basculait vers le
        // rendu single-text (scénario + texte), PERDANT V1/V2/V3/validation.
        // Correctif : on garde toujours la vue versionnée pour les scripts
        // avec versions ; l'édition passe par les boutons per-block
        // (openTurnsEditor v1/v2/v3, openValidationEditor). Scénario
        // devient éditable inline quand `editing=true`.
        //
        // Vincent B749=B809=B811 (Eric 2026-02-13) — Bug récurrent :
        // V3 « Justifié AMF » affichait uniquement le texte reformulé,
        // sans jamais rendre `justification` ni `objection_evitee` (les
        // champs SONT en DB mais `renderTurns` les ignorait). Idem le
        // tableau de validation mappait `row.v1/v2/v3` alors que la DB
        // stocke `phrase_reformule/justification/objection_evitee/
        // time_code/citation_originale` → toutes les cellules
        // fallback à "—". Fix : rendu aligné sur ScriptsPage.js.
        {
          const c = displayUnit.content || unit.content || {};
          const hasVersions = (c.v1_original && c.v1_original.length > 0) ||
                              (c.v2_reformule && c.v2_reformule.length > 0) ||
                              (c.v3_justifie && c.v3_justifie.length > 0);
          if (hasVersions) {
            // Vincent B749=B809=B811 — Renderer qui affiche les annotations
            // V3 (justification + objection évitée) sous chaque tour vendeur.
            // `withAnnotations` doit être passé à true pour V3.
            const isVendorRole = (r) => {
              const rs = String(r || "").toLowerCase();
              return ["vendeur", "csf", "conseiller", "vendor", "salesperson"].includes(rs);
            };
            const renderTurns = (turns, badgeColor, withAnnotations = false) =>
              (turns || []).map((t, i) => {
                const isVendor = isVendorRole(t.role);
                const roleLabel = isVendor ? "CSF" : (t.role === "client" ? "client" : (t.role === "mentor" ? "mentor" : (t.role || t.speaker || "—")));
                const text = t.text || t.content || "";
                const hasAnn = withAnnotations && isVendor && (t.justification || (t.objection_evitee && t.objection_evitee !== "aucune"));
                return (
                  <div key={i} className="px-3 py-2 border-b border-zinc-100 last:border-0">
                    <div className="flex items-baseline gap-2">
                      {t.time_code && (
                        <span className="text-[10px] font-mono text-zinc-400 tabular-nums shrink-0">{t.time_code}</span>
                      )}
                      <span className={`text-[10px] uppercase tracking-wide font-bold ${badgeColor} shrink-0`}>{roleLabel}</span>
                      <span className="text-sm leading-relaxed whitespace-pre-wrap">{text}</span>
                    </div>
                    {hasAnn && (
                      <ul className="ml-16 mt-1 space-y-0.5" data-testid={`v3-annot-${i}`}>
                        {t.objection_evitee && t.objection_evitee !== "aucune" && (
                          <li className="text-[11px] text-amber-700 italic flex items-start gap-1" data-testid="v3-objection-evitee">
                            <Shield size={11} weight="bold" className="mt-0.5 shrink-0" />
                            <span><span className="font-semibold not-italic text-zinc-700">{lang === "fr" ? "Objection évitée :" : "Avoided objection:"}</span> {t.objection_evitee}</span>
                          </li>
                        )}
                        {t.justification && (
                          <li className="text-[11px] text-teal-700 italic flex items-start gap-1" data-testid="v3-justification">
                            <Lightbulb size={11} weight="bold" className="mt-0.5 shrink-0" />
                            <span><span className="font-semibold not-italic text-zinc-700">{lang === "fr" ? "Pourquoi évitée efficacement :" : "Why effectively avoided:"}</span> {t.justification}</span>
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                );
              });
            return (
              <div className="space-y-3" data-testid={`script-versions-${unit.id}`}>
                {renderTitleEditBlock()}
                {/* Scénario — éditable inline en mode `editing` (B814b) */}
                <div>
                  <h4 className="text-xs font-medium text-muted-foreground mb-1">{tr("Scénario", "Scenario")}</h4>
                  {editing ? (
                    <textarea
                      value={editQ}
                      onChange={(e) => setEditQ(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                      data-testid="edit-ku-script-scenario"
                      placeholder={tr("Contexte / situation du script", "Context / situation of the script")}
                    />
                  ) : (
                    c.scenario ? <p className="text-sm">{c.scenario}</p> : null
                  )}
                </div>
                {/* Accordion v1 — Original
                    Vincent B814b (2026-02-13) — Bouton Éditer V1 restauré
                    (Vincent : « on avait toujours besoin de pouvoir éditer V1 »). */}
                {c.v1_original && c.v1_original.length > 0 && (
                  editingTurnsVersion === "v1" ? (
                    <TurnsInlineEditor
                      version="v1"
                      turns={draftTurns}
                      onChange={updateDraftTurn}
                      onAdd={addDraftTurn}
                      onRemove={removeDraftTurn}
                      onMove={moveDraftTurn}
                      onSave={saveTurns}
                      onCancel={() => { setEditingTurnsVersion(null); setDraftTurns([]); }}
                      saving={savingTurns}
                      lang={lang}
                    />
                  ) : (
                  <details className="bg-white rounded-lg border border-zinc-200" data-testid={`ku-script-v1-${unit.id}`}>
                    <summary className="cursor-pointer px-3 py-2 font-medium text-sm flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-wide bg-zinc-100 text-zinc-700 px-1.5 py-0.5 rounded font-bold">v1</span>
                      Original
                      <span className="text-[10px] text-zinc-500">({c.v1_original.length})</span>
                      {isAdmin && editing && (
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openTurnsEditor("v1"); }}
                          className="ml-auto text-[10px] px-2 py-0.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 rounded font-semibold"
                          data-testid={`edit-script-v1-${unit.id}`}
                          title={lang === "fr" ? "Éditer les tours V1" : "Edit V1 turns"}
                        >✏️ {lang === "fr" ? "Éditer V1" : "Edit V1"}</button>
                      )}
                    </summary>
                    <div className="border-t border-zinc-100">{renderTurns(c.v1_original, "text-zinc-600")}</div>
                  </details>
                  )
                )}
                {/* Accordion v2 — Reformulé
                    Vincent #270 (2026-06-12) — TOUJOURS rendre l'accordéon v2 :
                    s'il est vide, on l'indique EXPLICITEMENT au lieu de le
                    masquer (Vincent croyait la version "vide" = bug).
                    Vincent XLS iter103 (item 19) — Bouton "Éditer" admin pour
                    modifier les tours v2 (rôle + texte).
                    Vincent B814b — Le bouton n'est visible que quand `editing`
                    pour rester cohérent avec le nouveau workflow. */}
                {c.v2_reformule && c.v2_reformule.length > 0 ? (
                  editingTurnsVersion === "v2" ? (
                    <TurnsInlineEditor
                      version="v2"
                      turns={draftTurns}
                      onChange={updateDraftTurn}
                      onAdd={addDraftTurn}
                      onRemove={removeDraftTurn}
                      onMove={moveDraftTurn}
                      onSave={saveTurns}
                      onCancel={() => { setEditingTurnsVersion(null); setDraftTurns([]); }}
                      saving={savingTurns}
                      lang={lang}
                    />
                  ) : (
                  <details open className="bg-white rounded-lg border border-teal-200" data-testid={`ku-script-v2-${unit.id}`}>
                    <summary className="cursor-pointer px-3 py-2 font-medium text-sm flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-wide bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded font-bold">v2</span>
                      {tr("Reformulé", "Rephrased")}
                      <span className="text-[10px] text-zinc-500">({c.v2_reformule.length})</span>
                      {isAdmin && editing && (
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openTurnsEditor("v2"); }}
                          className="ml-auto text-[10px] px-2 py-0.5 bg-teal-100 hover:bg-teal-200 text-teal-800 rounded font-semibold"
                          data-testid={`edit-script-v2-${unit.id}`}
                          title={lang === "fr" ? "Éditer les tours V2" : "Edit V2 turns"}
                        >✏️ {lang === "fr" ? "Éditer V2" : "Edit V2"}</button>
                      )}
                    </summary>
                    <div className="border-t border-teal-100">{renderTurns(c.v2_reformule, "text-teal-700")}</div>
                  </details>
                  )
                ) : (
                  <div className="bg-zinc-50 rounded-lg border border-dashed border-zinc-300 px-3 py-2 flex items-center gap-2" data-testid={`ku-script-v2-empty-${unit.id}`}>
                    <span className="text-[10px] uppercase tracking-wide bg-zinc-200 text-zinc-500 px-1.5 py-0.5 rounded font-bold">v2</span>
                    <span className="text-xs text-zinc-500 italic">
                      Version reformulée non générée pour ce script — relancez la génération des scripts (page vidéo → onglet Scripts → Pipeline).
                    </span>
                  </div>
                )}
                {/* Accordion v3 — Justifié AMF — Vincent B749=B809=B811 (2026-02-13) :
                    rendu avec annotations `justification` + `objection_evitee`. */}
                {c.v3_justifie && c.v3_justifie.length > 0 ? (
                  editingTurnsVersion === "v3" ? (
                    <TurnsInlineEditor
                      version="v3"
                      turns={draftTurns}
                      onChange={updateDraftTurn}
                      onAdd={addDraftTurn}
                      onRemove={removeDraftTurn}
                      onMove={moveDraftTurn}
                      onSave={saveTurns}
                      onCancel={() => { setEditingTurnsVersion(null); setDraftTurns([]); }}
                      saving={savingTurns}
                      lang={lang}
                    />
                  ) : (
                  <details open className="bg-white rounded-lg border border-amber-200" data-testid={`ku-script-v3-${unit.id}`}>
                    <summary className="cursor-pointer px-3 py-2 font-medium text-sm flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold">v3</span>
                      Justification / objections
                      <span className="text-[10px] text-zinc-500">({c.v3_justifie.length})</span>
                      {isAdmin && editing && (
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openTurnsEditor("v3"); }}
                          className="ml-auto text-[10px] px-2 py-0.5 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded font-semibold"
                          data-testid={`edit-script-v3-${unit.id}`}
                          title={lang === "fr" ? "Éditer les tours V3" : "Edit V3 turns"}
                        >✏️ {lang === "fr" ? "Éditer V3" : "Edit V3"}</button>
                      )}
                    </summary>
                    <div className="border-t border-amber-100">{renderTurns(c.v3_justifie, "text-amber-700", true)}</div>
                  </details>
                  )
                ) : (
                  <div className="bg-zinc-50 rounded-lg border border-dashed border-zinc-300 px-3 py-2 flex items-center gap-2" data-testid={`ku-script-v3-empty-${unit.id}`}>
                    <span className="text-[10px] uppercase tracking-wide bg-zinc-200 text-zinc-500 px-1.5 py-0.5 rounded font-bold">v3</span>
                    <span className="text-xs text-zinc-500 italic">
                      Version justifiée AMF non générée pour ce script — relancez la génération des scripts (page vidéo → onglet Scripts → Pipeline).
                    </span>
                  </div>
                )}
                {/* Tableau de Validation — Vincent B749=B811 (2026-02-13) :
                    colonnes fixées (time_code / citation V1 / reformulé V2 /
                    justification+objection V3) alignées sur ScriptsPage.js.
                    Vincent B814b : édition inline restaurée. */}
                {editingValidation ? (
                  <ValidationTableEditor
                    rows={validationDraft}
                    onChange={updateValidationRow}
                    onAdd={addValidationRow}
                    onRemove={removeValidationRow}
                    onSave={saveValidation}
                    onCancel={() => { setEditingValidation(false); setValidationDraft([]); }}
                    saving={savingValidation}
                    lang={lang}
                  />
                ) : (c.validation_table && c.validation_table.length > 0) ? (
                  <details open className="bg-white rounded-lg border border-blue-200 overflow-hidden" data-testid={`ku-script-validation-${unit.id}`}>
                    <summary className="cursor-pointer px-3 py-2 font-medium text-sm flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-wide bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold">Validation</span>
                      {tr("Cohérence", "Consistency")}
                      <span className="text-[10px] text-zinc-500">({c.validation_table.length})</span>
                      {isAdmin && editing && (
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openValidationEditor(); }}
                          className="ml-auto text-[10px] px-2 py-0.5 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded font-semibold"
                          data-testid={`edit-script-validation-${unit.id}`}
                          title={lang === "fr" ? "Éditer le tableau de validation" : "Edit validation table"}
                        >✏️ {lang === "fr" ? "Éditer tableau" : "Edit table"}</button>
                      )}
                    </summary>
                    <div className="border-t border-blue-100 overflow-x-auto">
                      <table className="w-full text-xs" data-testid={`ku-script-validation-table-${unit.id}`}>
                        <thead className="bg-blue-50">
                          <tr>
                            <th className="text-left px-2 py-1.5 font-semibold text-blue-800 w-[10%]">{lang === "fr" ? "[HH:MM:SS]" : "[HH:MM:SS]"}</th>
                            <th className="text-left px-2 py-1.5 font-semibold text-blue-800 w-[28%]">{lang === "fr" ? "V1 — Citation originale (verbatim)" : "V1 — Original citation (verbatim)"}</th>
                            <th className="text-left px-2 py-1.5 font-semibold text-blue-800 w-[28%]">{lang === "fr" ? "V2 — Phrase reformulée" : "V2 — Reformulated phrase"}</th>
                            <th className="text-left px-2 py-1.5 font-semibold text-blue-800 w-[34%]">{lang === "fr" ? "V3 — Justification + objection évitée" : "V3 — Justification + avoided objection"}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {c.validation_table.map((row, i) => {
                            const hasV3 = (row.justification || "").trim().length > 0 || (row.objection_evitee || "").trim().length > 0;
                            return (
                              <tr key={i} className="border-t border-blue-50 align-top">
                                <td className="px-2 py-1.5 font-mono text-[11px] text-zinc-500">{row.time_code || "—"}</td>
                                <td className="px-2 py-1.5 text-zinc-700 italic bg-zinc-50/40">{row.citation_originale || "—"}</td>
                                <td className="px-2 py-1.5 text-zinc-800">{row.phrase_reformule || "—"}</td>
                                <td className="px-2 py-1.5 bg-amber-50/30">
                                  {hasV3 ? (
                                    <div className="space-y-1">
                                      {row.justification && (
                                        <div className="text-teal-700 italic flex items-start gap-1" data-testid={`val-justification-${i}`}>
                                          <Lightbulb size={11} weight="bold" className="mt-0.5 shrink-0" />
                                          <span><span className="font-semibold not-italic text-zinc-700">{lang === "fr" ? "Pourquoi :" : "Why:"}</span> {row.justification}</span>
                                        </div>
                                      )}
                                      {row.objection_evitee && row.objection_evitee !== "aucune" && (
                                        <div className="text-amber-700 italic flex items-start gap-1" data-testid={`val-objection-${i}`}>
                                          <Shield size={11} weight="bold" className="mt-0.5 shrink-0" />
                                          <span><span className="font-semibold not-italic text-zinc-700">{lang === "fr" ? "Objection évitée :" : "Avoided objection:"}</span> {row.objection_evitee}</span>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-zinc-400">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </details>
                ) : (
                  <div className="bg-zinc-50 rounded-lg border border-dashed border-zinc-300 px-3 py-2 flex items-center justify-between gap-2" data-testid={`ku-script-validation-empty-${unit.id}`}>
                    <span className="text-xs text-zinc-500 italic">
                      {lang === "fr"
                        ? "Tableau de validation non généré pour ce script — relancez la génération depuis la page vidéo → onglet Scripts → « Refaire validation »."
                        : "Validation table not generated for this script — re-run from the video page → Scripts tab → \"Redo validation\"."}
                    </span>
                    {isAdmin && editing && (
                      <button
                        onClick={openValidationEditor}
                        className="text-[10px] px-2 py-0.5 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded font-semibold shrink-0"
                        data-testid={`edit-script-validation-empty-${unit.id}`}
                      >
                        ✏️ {lang === "fr" ? "Saisir manuellement" : "Enter manually"}
                      </button>
                    )}
                  </div>
                )}
                {renderEditActions()}
              </div>
            );
          }
        }
        return (
          <div className="space-y-4">
            {renderTitleEditBlock()}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-1">{tr("Scénario", "Scenario")}</h4>
              {editing ? (
                <textarea
                  value={editQ}
                  onChange={(e) => setEditQ(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-base focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                  data-testid="edit-ku-script-scenario"
                  placeholder={tr("Contexte / situation du script", "Context / situation of the script")}
                />
              ) : (
                <p className="text-base">{displayUnit.content?.scenario}</p>
              )}
            </div>
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-1">{tr("Texte du script", "Script text")}</h4>
              {editing ? (
                <textarea
                  value={editA}
                  onChange={(e) => setEditA(e.target.value)}
                  rows={8}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-base focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                  data-testid="edit-ku-script-text"
                />
              ) : (
                <p className="text-base whitespace-pre-wrap">{displayUnit.content?.text || displayUnit.content?.content || displayUnit.content?.response}</p>
              )}
            </div>
            {renderEditActions()}
          </div>
        );
      case "objection":
        return (
          <div className="space-y-4">
            {renderTitleEditBlock()}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-1">Objection</h4>
              {editing ? (
                <textarea
                  value={editQ}
                  onChange={(e) => setEditQ(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-base focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                  data-testid="edit-ku-objection-text"
                  placeholder={tr("L'objection client telle qu'exprimée", "The client objection as it was voiced")}
                />
              ) : (
                <p className="text-base">{displayUnit.content?.objection || displayUnit.content?.question || displayUnit.content?.scenario}</p>
              )}
            </div>
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-1">{tr("Réponse", "Answer")}</h4>
              {editing ? (
                <textarea
                  value={editA}
                  onChange={(e) => setEditA(e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-base focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                  data-testid="edit-ku-objection-response"
                />
              ) : (
                <p className="text-base whitespace-pre-wrap">{displayUnit.content?.response || displayUnit.content?.phrase_evitement}</p>
              )}
            </div>
            {renderEditActions()}
          </div>
        );
      case "concept":
        return (
          <div className="space-y-4">
            {renderTitleEditBlock()}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-1">{tr("Définition", "Definition")}</h4>
              {editing ? (
                <textarea
                  value={editA}
                  onChange={(e) => setEditA(e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-base focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                  data-testid="edit-ku-concept-definition"
                />
              ) : (
                <p className="text-base">{displayUnit.content?.definition}</p>
              )}
            </div>
            {!editing && displayUnit.content?.key_points?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">{tr("Points clés", "Key points")}</h4>
                <ul className="space-y-1">
                  {displayUnit.content.key_points.map((point, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {editing && (
              /* Vincent #27 (Eric 2026-06-04) — Édition key_points en liste éditable */
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">{lang === "fr" ? "Points clés (liste)" : "Key points (list)"}</h4>
                <div className="space-y-2" data-testid="edit-ku-concept-keypoints">
                  {editKeyPoints.map((point, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={point}
                        onChange={(e) => {
                          const next = [...editKeyPoints];
                          next[i] = e.target.value;
                          setEditKeyPoints(next);
                        }}
                        className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm"
                        placeholder={lang === "fr" ? `Point ${i + 1}` : `Point ${i + 1}`}
                        data-testid={`edit-keypoint-input-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => setEditKeyPoints(editKeyPoints.filter((_, j) => j !== i))}
                        className="px-2 py-1 text-red-500 hover:bg-red-50 rounded"
                        title={lang === "fr" ? "Supprimer ce point" : "Remove this point"}
                        data-testid={`remove-keypoint-${i}`}
                      >
                        ✗
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setEditKeyPoints([...editKeyPoints, ""])}
                    className="px-3 py-1.5 text-xs rounded bg-blue-50 text-blue-700 hover:bg-blue-100"
                    data-testid="add-keypoint-btn"
                  >
                    + {lang === "fr" ? "Ajouter un point" : "Add point"}
                  </button>
                </div>
              </div>
            )}
            {renderEditActions()}
          </div>
        );
      case "synthesis":
        // Vincent B719+B721 (Eric 2026-02-11) — Rendu lisible d'une synthèse :
        // titre + corps markdown rendu + liste de points clés à retenir avec
        // rendu markdown inline (**gras**, *italique*, `code`). Remplace le
        // JSON brut du cas default.
        return (() => {
          const body = displayUnit.content?.body || "";
          const takeaways = Array.isArray(displayUnit.content?.key_takeaways) ? displayUnit.content.key_takeaways : [];
          // Rendu markdown block-level (headers, listes, gras, italique).
          const renderBlockMarkdown = (md) => {
            if (!md) return "";
            let h = String(md).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
            h = h.replace(/^## (.+)$/gm, '<h3 class="text-base font-semibold mt-4 mb-2 text-zinc-800">$1</h3>');
            h = h.replace(/^# (.+)$/gm, '<h2 class="text-lg font-bold mt-4 mb-2 text-zinc-900">$1</h2>');
            h = h.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-zinc-900">$1</strong>');
            h = h.replace(/(^|[^*])\*(?!\*)(.+?)\*(?!\*)/g, '$1<em>$2</em>');
            h = h.replace(/^- (.+)$/gm, '<li class="ml-5 list-disc">$1</li>');
            h = h.replace(/(<li[\s\S]*?<\/li>\n?)+/g, (m) => `<ul class="my-2 space-y-1">${m}</ul>`);
            h = h.replace(/\n\n/g, '</p><p class="mb-3">');
            h = `<p class="mb-3">${h}</p>`;
            return h;
          };
          const renderInline = (md) => {
            if (!md) return "";
            let h = String(md).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
            h = h.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-zinc-900">$1</strong>');
            h = h.replace(/(^|[^*])\*(?!\*)(.+?)\*(?!\*)/g, '$1<em>$2</em>');
            h = h.replace(/`([^`]+)`/g, '<code class="text-[12px] px-1 py-0.5 rounded bg-zinc-100 font-mono">$1</code>');
            return h;
          };
          return (
            <div className="space-y-4">
              {renderTitleEditBlock()}
              {editing ? (
                <>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-2 block">
                      {lang === "fr" ? "Contenu (markdown)" : "Content (markdown)"}
                    </label>
                    <textarea
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      rows={12}
                      className="w-full text-sm px-3 py-2 border border-zinc-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:border-transparent font-mono"
                      data-testid="ku-synthesis-body-editor"
                      placeholder={lang === "fr" ? "## Titre\n\n**Texte en gras**..." : "## Title\n\n**Bold text**..."}
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-muted-foreground">
                        {lang === "fr" ? "Points clés à retenir" : "Key takeaways"}
                        <span className="ml-1 text-xs text-zinc-400">({editTakeaways.length})</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setEditTakeaways([...editTakeaways, ""])}
                        className="px-2 py-1 rounded-md border border-teal-300 text-teal-700 text-xs font-medium hover:bg-teal-50"
                        data-testid="ku-synthesis-takeaway-add-btn"
                      >
                        + {lang === "fr" ? "Ajouter" : "Add"}
                      </button>
                    </div>
                    <ul className="space-y-2">
                      {editTakeaways.map((t, i) => (
                        <li key={i} className="flex items-start gap-2" data-testid={`ku-synthesis-takeaway-editor-${i}`}>
                          <span className="text-[#002FA7] font-semibold flex-shrink-0 pt-2">{i + 1}.</span>
                          <textarea
                            value={t}
                            onChange={(e) => setEditTakeaways(editTakeaways.map((x, xi) => xi === i ? e.target.value : x))}
                            rows={2}
                            className="flex-1 text-sm px-2 py-1.5 border border-zinc-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                            data-testid={`ku-synthesis-takeaway-input-${i}`}
                          />
                          <button
                            type="button"
                            onClick={() => setEditTakeaways(editTakeaways.filter((_, xi) => xi !== i))}
                            className="mt-1 p-1 rounded text-rose-500 hover:bg-rose-50 flex-shrink-0"
                            data-testid={`ku-synthesis-takeaway-remove-${i}`}
                            title={lang === "fr" ? "Supprimer ce point" : "Remove"}
                          >×</button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  {body && (
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-2">
                        {lang === "fr" ? "Contenu" : "Content"}
                      </h4>
                      <div
                        className="text-sm text-zinc-800 leading-relaxed"
                        data-testid="ku-synthesis-body"
                        dangerouslySetInnerHTML={{ __html: renderBlockMarkdown(body) }}
                      />
                    </div>
                  )}
                  {takeaways.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-2">
                        {lang === "fr" ? "Points clés à retenir" : "Key takeaways"}
                      </h4>
                      <ul className="space-y-2">
                        {takeaways.map((t, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-zinc-700">
                            <span className="text-[#002FA7] font-semibold flex-shrink-0">{i + 1}.</span>
                            <span dangerouslySetInnerHTML={{ __html: renderInline(t) }} />
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
              {renderEditActions()}
            </div>
          );
        })();
      default:
        return (
          <pre className="text-sm bg-secondary p-4 rounded-lg overflow-x-auto">
            {JSON.stringify(unit.content, null, 2)}
          </pre>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-card rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto animate-slide-up">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 z-10">
          {/* Vincent #314 (Eric 2026-06-18 SPEC STRICK ULTRA) — Le titre doit
              occuper la PLEINE LARGEUR de la fenêtre d'édition, SOUS la rangée
              des boutons d'action (Accepter/Refuser/Éditer/Historique/Effacer)
              et pas seulement sous le badge de type. */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 text-xs font-medium rounded-sm ${TYPE_STYLES[unit.type]}`}>
                {/* Vincent 2026-06-03 — Affichage canonique du type. */}
                {unit.type === "qa" ? "Q&R" : unit.type === "procedure" ? "Procédure" : unit.type === "script" ? "Script" : unit.type === "concept" ? "Concept" : unit.type === "objection" ? "Objection" : unit.type === "rebuttal" ? "Réfutation" : unit.type === "synthesis" ? "Synthèse" : unit.type}
              </span>
              {unit.edited_post_approval && (
                <span className="px-2 py-0.5 text-[10px] font-medium rounded-sm bg-amber-100 text-amber-800 border border-amber-200" title={tr("Édité après acceptation", "Edited after acceptance")}>
                  {tr("édité", "edited")}
                </span>
              )}
            </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Vincent (Eric 2026-02-06) — Cohérence des boutons pour TOUS les
                états (pending_review / approved / rejected). Le bouton de
                l'état actuel est SURLIGNÉ (filled). On peut toujours basculer
                d'un état à l'autre (re-approuver une refusée, re-refuser une
                approuvée, etc.). Audit Loi 25 conservé via review API. */}
            {isAdmin && !editing && ["qa", "concept", "script", "objection", "rebuttal", "procedure", "process"].includes(unit.type) && (() => {
              // Vincent XLSX 2026-07-09 #1+#2 (Eric 2026-07-09 SPEC STRICK) —
              // Optimistic UI : le badge Acceptée/Refusée bascule INSTANTANÉMENT
              // au clic, ET le bouton opposé perd sa couleur (vert/rouge) au
              // même instant. Auparavant : basé sur `unit.status` (prop) qui
              // n'était rafraîchi qu'après round-trip parent → décalage visuel.
              const effectiveStatus = pendingStatus || displayUnit.status || unit.status;
              const isApproved = effectiveStatus === "approved";
              const isRejected = effectiveStatus === "rejected";
              return (
                <>
                  <button
                    onClick={async () => {
                      if (isApproved) return;
                      setPendingStatus("approved");
                      try {
                        // Eric (2026-02-10 — Vague 1 P2) — Scripts (sales_scripts)
                        // n'utilisent pas /api/knowledge/{id}/review (dédié aux
                        // knowledge_units). On PATCH directement le doc avec status.
                        if (isScript) {
                          await axios.patch(ep.patch, { status: "approved" }, { withCredentials: true });
                        } else {
                          await axios.put(`${API}/api/knowledge/${unit.id}/review`, { action: "approve" }, { withCredentials: true });
                        }
                        setDisplayUnit((prev) => ({ ...prev, status: "approved" }));
                        toast.success(tr("Connaissance acceptée", "Knowledge accepted"));
                        onUpdated?.({ ...unit, status: "approved" });
                      } catch (err) {
                        setPendingStatus(null);
                        toast.error(err.response?.data?.detail || tr("Erreur", "Error"));
                      }
                    }}
                    className={`px-2.5 py-1.5 rounded-lg inline-flex items-center gap-1 text-xs font-semibold ${
                      isApproved
                        ? "bg-teal-600 text-white ring-2 ring-emerald-300 cursor-default"
                        : "border border-teal-300 text-teal-700 hover:bg-teal-50"
                    }`}
                    data-testid="ku-accept-btn"
                    title={isApproved
                      ? tr("État actuel : acceptée", "Current state: accepted")
                      : tr("Accepter cette connaissance", "Accept this knowledge")}
                  >
                    ✓ <span className="hidden sm:inline">{isApproved ? tr("Acceptée", "Accepted") : tr("Accepter", "Accept")}</span>
                  </button>
                  <button
                    onClick={async () => {
                      if (isRejected) return;
                      // Eric (2026-02-10 — Vague 1 P2) — Scripts n'ont pas d'état
                      // "rejected" : « Refuser » = retour à "pending" (révocation
                      // d'approbation), aligné avec ScriptsPage inline (toggle).
                      const rejectLabel = isScript
                        ? tr("Retirer l'approbation ? Le script repassera en statut « en révision ».", "Withdraw the approval? The script goes back to “under review”.")
                        : tr("Refuser cette connaissance ? Elle sera marquée « Refusée » (rouge) mais conservée pour audit (Loi 25).", "Reject this knowledge? It will be marked “Rejected” (red) but kept for audit (Law 25).");
                      const ok = await confirmDialog(
                        rejectLabel,
                        { danger: true, confirmLabel: isScript ? tr("Retirer", "Withdraw") : tr("Refuser", "Reject") },
                      );
                      if (!ok) return;
                      setPendingStatus(isScript ? "pending_review" : "rejected");
                      try {
                        if (isScript) {
                          await axios.patch(ep.patch, { status: "pending" }, { withCredentials: true });
                        } else {
                          await axios.put(`${API}/api/knowledge/${unit.id}/review`, { action: "reject" }, { withCredentials: true });
                        }
                        setDisplayUnit((prev) => ({ ...prev, status: isScript ? "pending_review" : "rejected" }));
                        toast.success(isScript
                          ? tr("Approbation retirée", "Approval withdrawn")
                          : tr("Connaissance refusée", "Knowledge rejected"));
                        onUpdated?.({ ...unit, status: isScript ? "pending_review" : "rejected" });
                      } catch (err) {
                        setPendingStatus(null);
                        toast.error(err.response?.data?.detail || tr("Erreur", "Error"));
                      }
                    }}
                    className={`px-2.5 py-1.5 rounded-lg inline-flex items-center gap-1 text-xs font-semibold ${
                      isRejected
                        ? "bg-rose-600 text-white ring-2 ring-rose-300 cursor-default"
                        : "border border-rose-300 text-rose-700 hover:bg-rose-50"
                    }`}
                    data-testid="ku-refuse-btn"
                    title={isRejected
                      ? tr("État actuel : refusée", "Current state: rejected")
                      : (isScript
                        ? tr("Retirer l'approbation", "Withdraw the approval")
                        : tr("Refuser cette connaissance", "Reject this knowledge"))}
                  >
                    ✗ <span className="hidden sm:inline">{isRejected ? tr("Refusée", "Rejected") : (isScript ? tr("Retirer", "Withdraw") : tr("Refuser", "Reject"))}</span>
                  </button>
                </>
              );
            })()}
            {/* Vincent (Eric 2026-05-16) — Édition étendue à TOUS les types
                (Q&R, Procédure, Script, Objection, Concept). Auparavant
                Vincent ne pouvait éditer que les Q&R, pas les autres. */}
            {isAdmin && ["qa", "concept", "script", "objection", "rebuttal", "procedure", "process", "synthesis"].includes(unit.type) && !editing && (
              <button
                onClick={() => {
                  setEditing(true);
                  setEditTitle(unit.title || "");
                  const t = (unit.type || "").toLowerCase();
                  // Seed editQ / editA depending on the type
                  if (t === "qa") {
                    setEditQ(unit.content?.question || "");
                    setEditA(unit.content?.answer || "");
                  } else if (t === "synthesis") {
                    // Vincent B726 (Eric 2026-02-12) — Seed body + takeaways
                    setEditBody(unit.content?.body || "");
                    setEditTakeaways(Array.isArray(unit.content?.key_takeaways) ? [...unit.content.key_takeaways] : []);
                  } else if (t === "procedure" || t === "process") {
                    setEditQ(unit.content?.summary || "");  // summary becomes the "intro"
                    const steps = unit.content?.steps || [];
                    setEditA(steps.map((s, i) => `${i + 1}. ${s}`).join("\n"));
                  } else if (t === "script") {
                    setEditQ(unit.content?.scenario || "");
                    setEditA(unit.content?.text || unit.content?.content || "");
                  } else if (t === "objection") {
                    setEditQ(unit.content?.objection || unit.content?.question || "");
                    setEditA(unit.content?.response || unit.content?.phrase_evitement || "");
                  } else if (t === "concept") {
                    setEditQ("");  // no "question" for concept
                    setEditA(unit.content?.definition || "");
                  }
                  setTitleManuallyEdited(false);
                }}
                className="p-2 hover:bg-secondary rounded-lg text-[#002FA7] inline-flex items-center gap-1 text-xs font-semibold"
                data-testid="edit-ku-btn"
                title={tr("Éditer cette connaissance", "Edit this knowledge")}
              >
                <PencilSimple size={16} />
                <span className="hidden sm:inline">{tr("Éditer", "Edit")}</span>
              </button>
            )}
            {/* Vincent #354 (Eric 2026-06-18 SPEC STRICK ULTRA) — Bouton
                Historique visible UNIQUEMENT pour les utilisateurs autorisés à
                éditer (isAdmin) — un consultateur n'a pas besoin du journal
                d'audit ni des avant/après des modifications. */}
            {isAdmin && ["qa", "concept", "script", "objection", "rebuttal", "procedure", "process"].includes(unit.type) && !editing && (
              <button
                onClick={loadHistory}
                disabled={loadingHistory}
                className="p-2 hover:bg-secondary rounded-lg text-amber-700 inline-flex items-center gap-1 text-xs font-semibold disabled:opacity-50"
                data-testid="ku-history-btn"
                title={tr("Voir l'historique des actions (Loi 25)", "View the action history (Law 25)")}
              >
                <Clock size={16} />
                <span className="hidden sm:inline">Historique</span>
              </button>
            )}
            {/* Vincent #279 (2026-06-11) — Bouton "Effacer" : place la tuile
                dans la CORBEILLE (soft delete, restaurable). Une 2e suppression
                depuis la corbeille est définitive. Confirmation obligatoire.
                Eric (2026-02-10 — Vague 1 P2) — Masqué pour les scripts :
                l'endpoint `/api/agent/knowledge-units/{id}` DELETE ne
                s'applique pas à `sales_scripts`. Vague 2 : unifier via
                `DELETE /api/scripts/{id}` ou endpoint hybride. */}
            {isAdmin && !editing && !isScript && (
              <button
                onClick={async () => {
                  const ok = await confirmDialog(
                    "Effacer cette connaissance ? Elle sera placée dans la CORBEILLE (récupérable). Pour l'effacer définitivement, il faudra la supprimer à nouveau depuis la corbeille.",
                    { danger: true, confirmLabel: "Placer dans la corbeille" },
                  );
                  if (!ok) return;
                  try {
                    await axios.delete(`${API}/api/agent/knowledge-units/${unit.id}`, { withCredentials: true });
                    toast.success(tr("Connaissance placée dans la corbeille", "Knowledge moved to the trash"));
                    onDeleted?.(unit.id);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || tr("Échec de l'effacement", "Deletion failed"));
                  }
                }}
                className="p-2 hover:bg-rose-50 rounded-lg text-rose-600 inline-flex items-center gap-1 text-xs font-semibold"
                data-testid="ku-delete-btn"
                title={tr("Effacer (placer dans la corbeille)", "Delete (move to the trash)")}
              >
                <Trash size={16} />
                <span className="hidden sm:inline">{tr("Effacer", "Delete")}</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-secondary rounded-lg -mt-1 -mr-2"
              data-testid="close-knowledge-modal"
            >
              <X size={20} />
            </button>
          </div>
          </div>
          {/* Enhancement B621.b (Eric 2026-07-30) — Navigation prev/next
              entre items siblings (par ex. Q&R d'une même vidéo). Affichée
              SEULEMENT quand au moins un des callbacks est fourni.
              Placée sous les actions, avant le titre, pour ne pas encombrer
              la ligne d'actions déjà chargée. */}
          {(onNavigatePrev || onNavigateNext) && (
            <div
              className="flex items-center gap-2 mt-2 mb-1"
              data-testid="ku-modal-nav-bar"
            >
              <button
                type="button"
                onClick={onNavigatePrev}
                disabled={!onNavigatePrev}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-zinc-200 bg-white hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="ku-modal-nav-prev"
                title={lang === "fr" ? "Précédent" : "Previous"}
              >
                <CaretLeft size={14} weight="bold" />
                {lang === "fr" ? "Précédent" : "Previous"}
              </button>
              {navigationLabel && (
                <span
                  className="text-xs text-zinc-500 font-medium tabular-nums"
                  data-testid="ku-modal-nav-label"
                >
                  {navigationLabel}
                </span>
              )}
              <button
                type="button"
                onClick={onNavigateNext}
                disabled={!onNavigateNext}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-zinc-200 bg-white hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="ku-modal-nav-next"
                title={lang === "fr" ? "Suivant" : "Next"}
              >
                {lang === "fr" ? "Suivant" : "Next"}
                <CaretRight size={14} weight="bold" />
              </button>
            </div>
          )}
          {/* Vincent #314 (Eric 2026-06-18 SPEC STRICK ULTRA) — Titre PLEINE LARGEUR sous les boutons d'action */}
          <h2 className="text-lg font-semibold font-['Outfit'] break-words w-full" data-testid="ku-detail-title">{displayUnit.title || "Unité de connaissance"}</h2>
        </div>
        
        <div className="p-6">
          {renderContent()}
        </div>
        
        <div className="border-t border-border px-6 py-3 bg-secondary/30">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex items-center gap-4">
              {/* Vincent #135 (Eric 2026-05-13) — Référence cliquable dans modal. */}
              {unit.video_id ? (
                <button
                  type="button"
                  onClick={() => {
                    const start = unit.timestamps?.start;
                    const t = start != null ? `&t=${Math.max(0, Math.floor(start))}` : "";
                    const tab = unit.type === "qa" ? "qa"
                      : unit.type === "procedure" || unit.type === "process" ? "procedures"
                      : unit.type === "script" ? "scripts"
                      : unit.type === "objection" || unit.type === "rebuttal" ? "objections"
                      : "knowledge";
                    window.location.href = `/dashboard/videos/${unit.video_id}?tab=${tab}${t}`;
                  }}
                  className="flex items-center gap-1 hover:text-[#002FA7] hover:underline"
                  data-testid={`modal-ref-video-link-${unit.id}`}
                  title={tr("Ouvrir la vidéo à ce moment", "Open the video at this timestamp")}
                >
                  <VideoCamera size={16} />
                  <span className="truncate max-w-[260px]">{videoTitle}</span>
                </button>
              ) : (
                <span className="flex items-center gap-1 italic text-zinc-300">
                  <VideoCamera size={16} /> vidéo introuvable
                </span>
              )}
              {unit.timestamps?.start != null && (
                <span className="flex items-center gap-1 font-mono">
                  <Clock size={16} />
                  {formatTimestamp(unit.timestamps.start)} - {formatTimestamp(unit.timestamps.end)}
                </span>
              )}
            </div>
            {unit.confidence && (
              <span>{Math.round(unit.confidence * 100)}% confiance</span>
            )}
          </div>
        </div>

        {/* Comments Section
            Eric (2026-02-10 — Vague 1 P2) — Masquée pour les scripts :
            l'endpoint `/api/knowledge/{id}/comments` cible knowledge_units.
            Vague 2 : ajouter le support commentaires pour sales_scripts. */}
        {!isScript && (
        <div className="border-t border-border px-6 py-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <ChatCircle size={16} />
            Discussion ({comments.length})
          </h3>

          {/* New comment input */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && postComment()}
              placeholder={tr("Ajouter un commentaire...", "Add a comment...")}
              className="flex-1 h-9 px-3 text-sm bg-secondary border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid="comment-input"
            />
            <button
              onClick={postComment}
              disabled={posting || !newComment.trim()}
              className="h-9 px-4 bg-primary text-primary-foreground text-sm rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity font-medium"
              data-testid="post-comment-btn"
            >
              {posting ? "…" : tr("Envoyer", "Send")}
            </button>
          </div>

          {/* Comments list */}
          {loadingComments ? (
            <div className="text-center py-4"><SpinnerGap size={20} className="animate-spin text-primary mx-auto" /></div>
          ) : comments.length > 0 ? (
            <div className="space-y-3 max-h-48 overflow-y-auto">
              {comments.map((c) => (
                <div key={c.id} className="flex gap-2.5 group" data-testid={`comment-${c.id}`}>
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold flex-shrink-0">
                    {c.user_name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold">{c.user_name}</span>
                      {c.display_role && (
                        <span className="text-[10px] px-1 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">{c.display_role}</span>
                      )}
                      <span className="text-[10px] text-muted-foreground">
                        {c.created_at ? new Date(c.created_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                      </span>
                      <button
                        onClick={() => deleteComment(c.id)}
                        className="opacity-0 group-hover:opacity-100 ml-auto text-muted-foreground hover:text-destructive transition-all"
                        data-testid={`delete-comment-${c.id}`}
                      >
                        <Trash size={12} />
                      </button>
                    </div>
                    <p className="text-sm text-foreground/80 mt-0.5">{c.text}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground text-center py-2">{tr("Aucun commentaire. Soyez le premier !", "No comment yet. Be the first!")}</p>
          )}
        </div>
        )}
      </div>

      {/* 2026-05-24 hotfix — Modal de confirmation "Tout effacer l'historique" (remplace confirm() natif bloqué dans Preview) */}
      {showClearHistoryConfirm && (
        <div
          className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
          onClick={() => setShowClearHistoryConfirm(false)}
          data-testid="clear-history-confirm-overlay"
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-zinc-900 mb-2">
              {lang === "fr" ? "Effacer l'historique des titres ?" : "Clear title history?"}
            </h3>
            <p className="text-sm text-zinc-600 mb-4">
              {lang === "fr"
                ? "Cette action supprimera définitivement tous les anciens titres archivés pour cette connaissance. Le titre actuel reste inchangé."
                : "This permanently removes all archived titles for this knowledge. Current title stays untouched."}
            </p>
            {titleHistory.length > 0 && (
              <p className="text-xs text-zinc-500 mb-5">
                {titleHistory.length} {lang === "fr" ? "entrée(s) à supprimer" : "entry(ies) to delete"}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowClearHistoryConfirm(false)}
                className="px-3 py-1.5 text-sm bg-zinc-100 text-zinc-700 rounded hover:bg-zinc-200"
                data-testid="clear-history-cancel"
              >
                {lang === "fr" ? "Annuler" : "Cancel"}
              </button>
              <button
                onClick={handleClearHistoryConfirmed}
                className="px-3 py-1.5 text-sm bg-rose-600 text-white rounded hover:bg-rose-700 font-medium"
                data-testid="clear-history-confirm"
              >
                🗑 {lang === "fr" ? "Oui, tout effacer" : "Yes, clear all"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// Vincent XLS iter103 (item 19) — Éditeur inline pour les tours v2/v3 d'un
// script de vente. Chaque tour = {role, text, [time_code], [justification],
// [objection_evitee]}. UI : liste de cartes avec champ rôle + textarea +
// boutons monter/descendre/supprimer par tour + bouton "Ajouter" en bas.
// Save = PATCH avec le tableau complet (idempotent — remplace l'existant).
// Vincent B814b (Eric 2026-02-13) — Support V1 en plus de V2/V3, ET
// exposition des champs `justification` + `objection_evitee` pour V3 pour
// permettre l'édition complète (Vincent : « on avait besoin de pouvoir
// modifier V1, V2, V3 »).
function TurnsInlineEditor({ version, turns, onChange, onAdd, onRemove, onMove, onSave, onCancel, saving, lang }) {
  const tr = (fr, en) => (lang === "en" ? en : fr);
  const isV1 = version === "v1";
  const isV2 = version === "v2";
  const isV3 = version === "v3";
  const accentBg = isV1 ? "bg-zinc-50" : (isV2 ? "bg-teal-50" : "bg-amber-50");
  const accentBorder = isV1 ? "border-zinc-300" : (isV2 ? "border-teal-300" : "border-amber-300");
  const accentText = isV1 ? "text-zinc-700" : (isV2 ? "text-teal-700" : "text-amber-700");
  const accentBadge = isV1 ? "bg-zinc-200 text-zinc-700" : (isV2 ? "bg-teal-100 text-teal-700" : "bg-amber-100 text-amber-700");
  const versionLabel = isV1 ? "Original" : (isV2 ? "Reformulé" : "Justification / objections");
  const saveBtnColor = isV1 ? "bg-zinc-700 hover:bg-zinc-800" : (isV2 ? "bg-teal-600 hover:bg-teal-700" : "bg-amber-600 hover:bg-amber-700");
  return (
    <div className={`rounded-lg border-2 ${accentBorder} ${accentBg} p-3`} data-testid={`turns-editor-${version}`}>
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-zinc-200">
        <span className={`text-[10px] uppercase tracking-wide ${accentBadge} px-1.5 py-0.5 rounded font-bold`}>{version}</span>
        <span className="text-sm font-semibold text-zinc-800">{versionLabel} — {lang === "fr" ? "édition" : "edit"}</span>
        <span className="text-[10px] text-zinc-500">({turns.length} {lang === "fr" ? "tours" : "turns"})</span>
        <div className="ml-auto flex gap-1.5">
          <button
            onClick={onCancel}
            disabled={saving}
            className="text-xs px-3 py-1 bg-zinc-100 hover:bg-zinc-200 rounded font-medium disabled:opacity-50"
            data-testid={`turns-editor-cancel-${version}`}
          >
            {lang === "fr" ? "Annuler" : "Cancel"}
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className={`text-xs px-3 py-1 ${saveBtnColor} text-white rounded font-semibold disabled:opacity-50`}
            data-testid={`turns-editor-save-${version}`}
          >
            {saving ? (lang === "fr" ? "⏳ ..." : "⏳ ...") : (lang === "fr" ? "💾 Enregistrer" : "💾 Save")}
          </button>
        </div>
      </div>
      <div className="space-y-2" data-testid={`turns-editor-list-${version}`}>
        {turns.map((t, idx) => (
          <div key={idx} className="bg-white rounded border border-zinc-200 p-2" data-testid={`turn-editor-row-${version}-${idx}`}>
            <div className="flex items-center gap-1.5 mb-1.5">
              <input
                type="text"
                value={t.role || ""}
                onChange={(e) => onChange(idx, { role: e.target.value })}
                placeholder={lang === "fr" ? "Rôle (CSF, client, mentor)" : "Role (CSF, client, mentor)"}
                className={`w-32 text-xs px-2 py-1 border border-zinc-200 rounded ${accentText} font-bold uppercase tracking-wide`}
                data-testid={`turn-role-input-${version}-${idx}`}
              />
              <input
                type="text"
                value={t.time_code || ""}
                onChange={(e) => onChange(idx, { time_code: e.target.value })}
                placeholder="[HH:MM:SS]"
                className="w-28 text-[11px] px-2 py-1 border border-zinc-200 rounded font-mono text-zinc-500"
                data-testid={`turn-tc-input-${version}-${idx}`}
              />
              <div className="flex-1" />
              <button
                onClick={() => onMove(idx, -1)}
                disabled={idx === 0}
                className="text-xs px-1.5 py-1 bg-zinc-100 hover:bg-zinc-200 rounded disabled:opacity-30"
                title={lang === "fr" ? "Monter" : "Move up"}
              >↑</button>
              <button
                onClick={() => onMove(idx, 1)}
                disabled={idx === turns.length - 1}
                className="text-xs px-1.5 py-1 bg-zinc-100 hover:bg-zinc-200 rounded disabled:opacity-30"
                title={lang === "fr" ? "Descendre" : "Move down"}
              >↓</button>
              <button
                onClick={() => onRemove(idx)}
                className="text-xs px-1.5 py-1 bg-rose-100 hover:bg-rose-200 text-rose-700 rounded"
                title={lang === "fr" ? "Supprimer ce tour" : "Delete this turn"}
                data-testid={`turn-delete-${version}-${idx}`}
              >🗑</button>
            </div>
            <textarea
              value={t.text || ""}
              onChange={(e) => onChange(idx, { text: e.target.value })}
              placeholder={lang === "fr" ? "Texte du tour…" : "Turn text…"}
              rows={Math.max(2, Math.min(8, ((t.text || "").split("\n").length || 1) + 1))}
              className="w-full text-sm px-2 py-1.5 border border-zinc-200 rounded resize-y leading-relaxed"
              data-testid={`turn-text-input-${version}-${idx}`}
            />
            {/* Vincent B814b — Champs V3 : justification + objection évitée éditables */}
            {isV3 && (
              <div className="mt-2 space-y-1.5">
                <div>
                  <label className="text-[10px] uppercase tracking-wide font-semibold text-teal-700">
                    {lang === "fr" ? "Justification (pourquoi ça marche)" : "Justification (why it works)"}
                  </label>
                  <textarea
                    value={t.justification || ""}
                    onChange={(e) => onChange(idx, { justification: e.target.value })}
                    rows={2}
                    className="w-full text-[12px] px-2 py-1 border border-teal-200 rounded resize-y bg-teal-50/40"
                    data-testid={`turn-justification-input-v3-${idx}`}
                    placeholder={lang === "fr" ? "1 phrase concise, orientée AMF / vente" : "1 concise sentence, AMF / sales-oriented"}
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wide font-semibold text-amber-700">
                    {lang === "fr" ? "Objection évitée" : "Avoided objection"}
                  </label>
                  <textarea
                    value={t.objection_evitee || ""}
                    onChange={(e) => onChange(idx, { objection_evitee: e.target.value })}
                    rows={2}
                    className="w-full text-[12px] px-2 py-1 border border-amber-200 rounded resize-y bg-amber-50/40"
                    data-testid={`turn-objection-input-v3-${idx}`}
                    placeholder={lang === "fr" ? "« Objection formulée par le client » — ou 'aucune'" : "\"Objection expressed by the client\" — or 'none'"}
                  />
                </div>
              </div>
            )}
          </div>
        ))}
        <button
          onClick={onAdd}
          className={`w-full text-sm py-2 border-2 border-dashed ${accentBorder} ${accentText} rounded hover:${accentBg} font-medium`}
          data-testid={`turns-editor-add-${version}`}
        >
          ➕ {lang === "fr" ? "Ajouter un tour" : "Add a turn"}
        </button>
      </div>
    </div>
  );
}

// Vincent B814b (Eric 2026-02-13) — Éditeur inline pour le `validation_table`
// d'un script de vente. 5 colonnes : time_code, citation_originale (V1),
// phrase_reformule (V2), justification (V3), objection_evitee (V3).
// Save = PATCH avec le tableau complet.
function ValidationTableEditor({ rows, onChange, onAdd, onRemove, onSave, onCancel, saving, lang }) {
  const tr = (fr, en) => (lang === "en" ? en : fr);
  return (
    <div className="rounded-lg border-2 border-blue-300 bg-blue-50 p-3" data-testid="validation-table-editor">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-zinc-200">
        <span className="text-[10px] uppercase tracking-wide bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold">Validation</span>
        <span className="text-sm font-semibold text-zinc-800">
          {lang === "fr" ? "Cohérence — édition" : "Coherence — edit"}
        </span>
        <span className="text-[10px] text-zinc-500">({rows.length} {lang === "fr" ? "lignes" : "rows"})</span>
        <div className="ml-auto flex gap-1.5">
          <button
            onClick={onCancel}
            disabled={saving}
            className="text-xs px-3 py-1 bg-zinc-100 hover:bg-zinc-200 rounded font-medium disabled:opacity-50"
            data-testid="validation-editor-cancel"
          >{lang === "fr" ? "Annuler" : "Cancel"}</button>
          <button
            onClick={onSave}
            disabled={saving}
            className="text-xs px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold disabled:opacity-50"
            data-testid="validation-editor-save"
          >{saving ? "⏳ ..." : (lang === "fr" ? "💾 Enregistrer" : "💾 Save")}</button>
        </div>
      </div>
      <div className="space-y-3" data-testid="validation-editor-list">
        {rows.map((r, i) => (
          <div key={i} className="bg-white rounded border border-zinc-200 p-2 grid grid-cols-1 md:grid-cols-12 gap-1.5" data-testid={`validation-row-${i}`}>
            <div className="md:col-span-2 flex items-start gap-1">
              <input
                type="text"
                value={r.time_code || ""}
                onChange={(e) => onChange(i, { time_code: e.target.value })}
                placeholder="[HH:MM:SS]"
                className="flex-1 text-[11px] px-2 py-1 border border-zinc-200 rounded font-mono text-zinc-500"
                data-testid={`validation-tc-${i}`}
              />
              <button
                onClick={() => onRemove(i)}
                className="text-xs px-1.5 py-1 bg-rose-100 hover:bg-rose-200 text-rose-700 rounded shrink-0"
                title={lang === "fr" ? "Supprimer" : "Delete"}
                data-testid={`validation-delete-${i}`}
              >🗑</button>
            </div>
            <div className="md:col-span-3">
              <label className="text-[9px] uppercase tracking-wide font-bold text-zinc-500">V1 — Citation</label>
              <textarea
                value={r.citation_originale || ""}
                onChange={(e) => onChange(i, { citation_originale: e.target.value })}
                placeholder={tr("[HH:MM:SS] Prénom: « ... »", "[HH:MM:SS] First name: “...”")}
                rows={3}
                className="w-full text-[11.5px] px-2 py-1 border border-zinc-200 rounded resize-y italic"
                data-testid={`validation-citation-${i}`}
              />
            </div>
            <div className="md:col-span-3">
              <label className="text-[9px] uppercase tracking-wide font-bold text-teal-600">{tr("V2 — Reformulé", "V2 — Rephrased")}</label>
              <textarea
                value={r.phrase_reformule || ""}
                onChange={(e) => onChange(i, { phrase_reformule: e.target.value })}
                rows={3}
                className="w-full text-[11.5px] px-2 py-1 border border-teal-200 rounded resize-y"
                data-testid={`validation-reformule-${i}`}
              />
            </div>
            <div className="md:col-span-4 space-y-1">
              <div>
                <label className="text-[9px] uppercase tracking-wide font-bold text-teal-700">V3 — Justification</label>
                <textarea
                  value={r.justification || ""}
                  onChange={(e) => onChange(i, { justification: e.target.value })}
                  rows={2}
                  className="w-full text-[11.5px] px-2 py-1 border border-teal-200 rounded resize-y bg-teal-50/40"
                  data-testid={`validation-justification-${i}`}
                />
              </div>
              <div>
                <label className="text-[9px] uppercase tracking-wide font-bold text-amber-700">{tr("V3 — Objection évitée", "V3 — Objection avoided")}</label>
                <textarea
                  value={r.objection_evitee || ""}
                  onChange={(e) => onChange(i, { objection_evitee: e.target.value })}
                  rows={2}
                  className="w-full text-[11.5px] px-2 py-1 border border-amber-200 rounded resize-y bg-amber-50/40"
                  data-testid={`validation-objection-${i}`}
                />
              </div>
            </div>
          </div>
        ))}
        <button
          onClick={onAdd}
          className="w-full text-sm py-2 border-2 border-dashed border-blue-300 text-blue-700 rounded hover:bg-blue-50 font-medium"
          data-testid="validation-editor-add"
        >
          ➕ {lang === "fr" ? "Ajouter une ligne" : "Add a row"}
        </button>
      </div>
    </div>
  );
}